import React, { useEffect, useState, useCallback } from 'react';
import { createStyles, withStyles } from '@material-ui/core/styles';
import ErrorHandler from '../../utils/ErrorHandler';
import ProgressIndicator from '../progressIndicator/ProgressIndicator';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { AlertBanner } from '../primitives';
import detailsPageStyle from '../commonStyles/detailsPage.style';
import filterBadgeStyle from './filterBadge.style';
import clx from 'classnames';
import colors from '../../utils/colors';
import configurationUtils from '../../utils/configurationUtils';
import _, { debounce } from 'lodash';
import queryString from 'query-string';
import browserHistory from '../../utils/browserHistory';
import { formatLabel } from '../containers/Containers';
import {
  AuthRoutes,
  stationDashboardUrlId,
} from '../../interfaces/routes';
import AsyncSelect from 'react-select/async';
import asyncSelectStyles from '../asyncSelect/asyncSelect.styles';
import { noOptionsMessage } from '../../components/asyncSelect/utils';
import { components } from 'react-select';

//Redux
import { useAppSelector, useAppDispatch } from '../../redux/store';
import {
  selectGlobalFilters,
  updateFiltersOptions,
  updateFiltersState,
} from '../../redux/slices/filtersSlice';

//Types
import { AxiosError } from 'axios';
import {
  Route,
  User,
  Location,
  Area,
  ContainerContainerTypeEnum,
  Customer,
  InboundLoadLoadStatusEnum,
  Organization,
} from 'cloudsort-client';
import { TypeAheadItem } from '../../interfaces/components';

// Icons
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ClearIcon from '@material-ui/icons/Clear';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// Services
import FmcService from '../../services/Fmc.service';
import CustomersService from '../../services/Customers.service';
import StationsService from '../../services/Stations.service';
import RoutesService from '../../services/Routes.service';
import AreasService from '../../services/Areas.service';
import EphemeralStateService from '../../services/EphemeralState.service';
import OrganizationsService from '../../services/Organizations.service';

interface Props {
  classes: { [key: string]: string };
  onAfterClose: () => void;
  isOpen: boolean;
  removeFilter?: { filterKey: string; filterValue: string };
  getFilters?: (filters: SelectedFilters) => void;
}

export interface FilterItem {
  key: string;
  label: string;
  visible: boolean;
  timeAdded: number;
  values: {
    id: string | number;
    name?: string;
    full_name?: string;
  }[];
}
export interface SelectedFilters {
  [key: string]: FilterItem;
}

export interface AvailableFilter {
  key: string;
  label: string;
  options: (
    | User
    | Route
    | Location
    | Area
    | { id: string | number; name: string }
  )[];
  singleSelection: boolean;
  async?: boolean;
}

const DropdownIndicator = (props: any) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon style={{ color: colors.white }} />
      </components.DropdownIndicator>
    )
  );
};

export const createFiltersQuery = (
  passedInFilters?: SelectedFilters,
  excludeFilters: string[] = [],
) => {
  return passedInFilters
    ? Object.entries(passedInFilters!)
        .map((item) => item[1])
        .filter(
          (item: FilterItem) => !excludeFilters.includes(item.key),
        )
        .reduce(
          (obj: any, item: any) => {
            if (item.values.length)
              return {
                ...obj,
                [item.key]: item.values
                  .map((v: any) => v.id)
                  .join(','),
              };
            else return obj;
          },
          {
            [stationDashboardUrlId]:
              EphemeralStateService.getMyStationId().toString(),
          },
        )
    : [];
};

export const getAppliedFilters = (
  passedInFilters?: SelectedFilters,
  excludeFilters: string[] = [],
): FilterItem[] => {
  return passedInFilters
    ? Object.entries(passedInFilters!)
        .map((item) => item[1])
        .filter(
          (item: FilterItem) =>
            !excludeFilters.includes(item.key) && item.values.length,
        )
    : [];
};

const FiltersDrawer: React.FC<Props> = ({
  isOpen,
  classes,
  onAfterClose,
  removeFilter,
  getFilters,
}) => {
  const [open, setOpen] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [error, setError] = useState<string>();
  const handleError = async (e: AxiosError) => {
    setError(await ErrorHandler.getLabel(e));
  };

  const handleDrawerClose = () => {
    setOpen(false);
    onAfterClose();
    setSelectedFilters(selectedFiltersInitial);
  };

  //Station Behaviours
  const stationBehaviors = configurationUtils
    .getConfigArray({
      config:
        EphemeralStateService.getMyStationConfiguratation().GENERAL
          .BEHAVIORS,
      filter: { key: 'active', value: true },
    })
    .map((item: any) => item.id);

  //OT Modes
  const otModes = configurationUtils
    .getConfigArray({
      config:
        EphemeralStateService.getMyStationConfiguratation().OT.MODES,
      filter: { key: 'active', value: true },
    })
    .map((item: any) => item.id);

  //States Config
  const statesConfig =
    EphemeralStateService.getMyStationConfiguratation()?.GENERAL
      ?.STATES || null;

  //Available Filters
  const [availableFilters, setAvailableFilers] =
    useState<AvailableFilter[]>();

  //Detect page
  let page:
    | 'dashboard'
    | 'containers'
    | 'packages'
    | 'loads'
    | 'manifests'
    | 'inbound_loads'
    | 'webhooks';

  const currentPathName = window.location.pathname;
  if (currentPathName.includes(AuthRoutes.DASHBOARD)) {
    page = 'dashboard';
  }
  if (currentPathName.includes(AuthRoutes.PACKAGE)) {
    page = 'packages';
  }
  if (currentPathName.includes(AuthRoutes.MANIFEST)) {
    page = 'manifests';
  }
  if (currentPathName.includes(AuthRoutes.CONTAINER)) {
    page = 'containers';
  }
  if (currentPathName.includes(AuthRoutes.OUTBOUND_LOAD)) {
    page = 'loads';
  }
  if (currentPathName.includes(AuthRoutes.INBOUND_LOAD)) {
    page = 'inbound_loads';
  }
  if (currentPathName.includes(AuthRoutes.WEBHOOKS)) {
    page = 'webhooks';
  }
  const baseQuery = queryString.parse(window.location.search);

  //Initial state
  const [selectedFiltersInitial, setSelectedFiltersInitial] =
    useState<SelectedFilters>();
  const [selectedFilters, setSelectedFilters] =
    useState<SelectedFilters>();

  const fetchOptionsByQuery = async (
    baseQueryStr: string,
    fetch: (param: any) => any,
  ) => {
    if (baseQueryStr) {
      let data;
      if (baseQueryStr.includes(',')) {
        const calls: any = [];
        baseQueryStr.split(',').forEach((id) => {
          calls.push(fetch(Number(id)));
        });
        const responses = await Promise.all(calls);
        data = responses.map((el: any) => ({
          id: el.data.id,
          name:
            el.data.full_name || el.data.name || el.data.identifier,
        }));
      } else {
        data = (await fetch(Number(baseQueryStr))).data;
        data = data
          ? [
              {
                id: data.id,
                name: data.full_name || data.name || data.identifier,
              },
            ]
          : [];
      }
      return data;
    }
    return [];
  };

  const fetchFilterOptions = async () => {
    try {
      //Carrier
      const fmcData = await fetchOptionsByQuery(
        baseQuery.carrier as string,
        (id) => FmcService.getById(Number(id)),
      );

      //Owner
      const ownerData = await fetchOptionsByQuery(
        baseQuery.owner as string,
        (id) => CustomersService.getById(Number(id)),
      );

      //Organization
      const organizationData = await fetchOptionsByQuery(
        baseQuery.organization as string,
        (id) => OrganizationsService.getById(Number(id)),
      );

      //Stop
      const stopData = await fetchOptionsByQuery(
        baseQuery.stop as string,
        (id) => StationsService.getLocationById(id),
      );

      //Route
      const routeData = await fetchOptionsByQuery(
        baseQuery.route as string,
        (id) => RoutesService.getById(Number(id)),
      );

      // Areas
      const areaData = baseQuery.area
        ? (await AreasService.getById(Number(baseQuery.area))).data
        : null;

      //Package Process Steps
      const processStepsOptionsConfig = configurationUtils
        .getConfigArray({
          config: EphemeralStateService.getMyStationConfiguratation()
            ? EphemeralStateService.getMyStationConfiguratation()
                ?.GENERAL?.PROCESS_STEPS
            : undefined,
          filter: { key: 'active', value: true },
        })
        .map((step) => {
          return { id: step.id, name: step.label };
        });

      //Manifested
      const yesNoOptions = [
        { id: 'yes', name: 'Yes' },
        { id: 'no', name: 'No' },
      ];

      // Package Defects
      const defectOptions = [
        { id: 'EXCEPTION_UNPROCESSABLE', name: 'Unprocessable' },
      ];
      if (stationBehaviors?.includes('MISSORTING'))
        defectOptions.push({
          id: 'EXCEPTION_MISSORT_FORCED',
          name: 'Missorted',
        });
      if (stationBehaviors?.includes('MISLOADING'))
        defectOptions.push({
          id: 'EXCEPTION_MISLOAD_FORCED',
          name: 'Misloaded',
        });

      if (otModes?.includes('RELABEL'))
        defectOptions.push({
          id: 'EXCEPTION_RELABELED',
          name: 'Relabeled',
        });

      if (otModes?.includes('RETURNING'))
        defectOptions.push({
          id: 'EXCEPTION_RETURNED',
          name: 'Returned',
        });

      // Container Defects
      const containerDefectOptions = [
        { id: 'EXCEPTION_PRINT_FORCED', name: 'Force Labeled' },
      ];
      if (stationBehaviors?.includes('MISMOVING'))
        containerDefectOptions.push({
          id: 'EXCEPTION_MISMOVE_FORCED',
          name: 'Force Moved',
        });
      if (stationBehaviors?.includes('MISLOADING'))
        containerDefectOptions.push({
          id: 'EXCEPTION_MISLOAD_FORCED',
          name: 'Force Loaded',
        });

      //Container Status
      const containerProcessStepOptions = configurationUtils
        .getContainerlStates()
        .map((state) => {
          return {
            id: state.key
              .replace('CONTAINER_', '')
              .replace('OPEN', 'OPENED'),
            name: state.label,
          };
        });

      //Container Format
      const formatOptions = [
        ...Object.keys(ContainerContainerTypeEnum).map((key) => {
          return {
            name: formatLabel(key),
            id: key,
          };
        }),
      ];

      //Container Type & Manifest Type
      const typeOptions = [
        { id: 'inbound', name: 'Inbound' },
        { id: 'outbound', name: 'Outbound' },
      ];

      //Outbound Load Status
      const loadStatusOptions = [
        { id: 'LOADING', name: statesConfig?.LOAD_LOADING?.label },
        {
          id: 'DISPATCHED',
          name: statesConfig?.LOAD_DISPATCHED?.label,
        },
      ];

      //Outbound Loads Defect
      const loadDefectOptions = [
        { id: 'EXCEPTION_DISPATCH_FORCED', name: 'Force Dispatched' },
      ];

      //Inbound Load Status
      const hiddenLoadStatuses = [
        InboundLoadLoadStatusEnum.UNLOADING,
      ];
      const inboundLoadStatusOptions = [];
      for (let status of Object.keys(
        InboundLoadLoadStatusEnum,
      ).filter(
        (status) =>
          !hiddenLoadStatuses.includes(
            InboundLoadLoadStatusEnum[
              status as keyof typeof InboundLoadLoadStatusEnum
            ],
          ),
      )) {
        inboundLoadStatusOptions.push({
          id: status,
          name:
            status.substring(0, 1).toUpperCase() +
            status.substring(1).toLowerCase(),
        });
      }

      const filterOptions: {
        [key: string]: AvailableFilter['options'];
      } = {
        carrier: fmcData,
        owner: ownerData,
        organization: organizationData,
        stop: stopData,
        route: routeData,
        area: areaData
          ? [{ id: areaData.id, name: areaData.name }]
          : [],
        processStep: processStepsOptionsConfig,
        packageManifested: yesNoOptions,
        packageScanned: yesNoOptions,
        packageScannedToday: yesNoOptions,
        packageArrived: yesNoOptions,
        packageInSystem: yesNoOptions,
        packageDefect: defectOptions,
        containerDefect: containerDefectOptions,
        containerProcessStep: containerProcessStepOptions,
        containerFormat: formatOptions,
        containerType: typeOptions,
        hidePending: yesNoOptions,
        loadStatus: loadStatusOptions,
        loadDefect: loadDefectOptions,
        manifestType: typeOptions,
        inboundLoadStatus: inboundLoadStatusOptions,
      };

      //Store filter options
      dispatch(updateFiltersOptions(filterOptions));

      return filterOptions;
    } catch (e) {
      handleError(e);
    } finally {
      setShowProgress(false);
    }
  };

  const initFilters = async () => {
    setShowProgress(true);

    const availableOptions = _.isEmpty(
      filtersReduxStore.filterOptions,
    )
      ? await fetchFilterOptions()
      : filtersReduxStore.filterOptions;

    const availableFilters = [];
    const selectedFilters: any = {};
    try {
      if (page !== 'inbound_loads' && page !== 'webhooks') {
        //Carrier
        availableFilters.push({
          key: 'carrier',
          label: 'Carrier',
          options: availableOptions!.carrier,
          singleSelection: false,
          async: true,
        });
        selectedFilters.carrier = {
          key: 'carrier',
          label: 'Carrier',
          visible: false,
          timeAdded: Date.now(),
          values: baseQuery.carrier
            ? availableOptions!.carrier.filter((option: any) =>
                (baseQuery.carrier as string)!
                  .split(',')
                  .includes(option.id!.toString()),
              )
            : [],
        };

        //Stop
        if (configurationUtils.isModuleActive('STOP')) {
          availableFilters.push({
            key: 'stop',
            label: configurationUtils.getPageTitle(true, 'STOP'),
            options: availableOptions!.stop,
            singleSelection: false,
            async: true,
          });
          selectedFilters.stop = {
            key: 'stop',
            label: configurationUtils.getPageTitle(true, 'STOP'),
            visible: false,
            timeAdded: Date.now(),
            values: baseQuery.stop
              ? availableOptions!.stop.filter((option: any) =>
                  (baseQuery.stop as string)!
                    .split(',')
                    .includes(option.id!.toString()),
                )
              : [],
          };
        }

        //Route
        if (configurationUtils.isModuleActive('ROUTE')) {
          availableFilters.push({
            key: 'route',
            label: configurationUtils.getPageTitle(true, 'ROUTE'),
            options: availableOptions!.route,
            singleSelection: false,
            async: true,
          });
          selectedFilters.route = {
            key: 'route',
            label: configurationUtils.getPageTitle(true, 'ROUTE'),
            visible: false,
            timeAdded: Date.now(),
            values: baseQuery.route
              ? availableOptions!.route.filter((option: any) =>
                  (baseQuery.route as string)!
                    .split(',')
                    .includes(option.id!.toString()),
                )
              : [],
          };
        }
      }

      //Owner
      availableFilters.push({
        key: 'owner',
        label: 'Owner',
        options: availableOptions!.owner,
        singleSelection: page === 'webhooks', //Single owner selection happens only on webhooks page
        async: true,
      });
      selectedFilters.owner = {
        key: 'owner',
        label: 'Owner',
        visible: page === 'webhooks' || page === 'inbound_loads',
        timeAdded: Date.now(),
        values: baseQuery.owner
          ? availableOptions!.owner.filter((option: any) =>
              (baseQuery.owner as string)!
                .split(',')
                .includes(option.id!.toString()),
            )
          : [],
      };

      if (page === 'webhooks') {
        //Organization
        availableFilters.push({
          key: 'organization',
          label: 'Organization',
          options: availableOptions!.organization,
          singleSelection: true,
          async: true,
        });
        selectedFilters.organization = {
          key: 'organization',
          label: 'Organization',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.organization
            ? availableOptions!.organization.filter((option: any) =>
                (baseQuery.organization as string)!
                  .split(',')
                  .includes(option.id!.toString()),
              )
            : [],
        };
      }

      // Hide Pending
      if (page !== 'manifests' && page !== 'webhooks') {
        availableFilters.push({
          key: 'hidePending',
          label: 'Hide Pending',
          options: availableOptions!.hidePending,
          singleSelection: true,
        });
        selectedFilters.hidePending = {
          key: 'hidePending',
          label: 'Hide Pending',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.hidePending
            ? availableOptions!.hidePending.filter((option: any) =>
                (baseQuery.hidePending as string)!
                  .split(',')
                  .includes(option.id),
              )
            : [],
        };
      }

      // Status
      if (page === 'inbound_loads') {
        availableFilters.push({
          key: 'inboundLoadStatus',
          label: 'Status',
          options: availableOptions!.inboundLoadStatus,
          singleSelection: true,
        });
        selectedFilters.inboundLoadStatus = {
          key: 'inboundLoadStatus',
          label: 'Status',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.inboundLoadStatus
            ? availableOptions!.inboundLoadStatus.filter(
                (option: any) =>
                  (baseQuery.inboundLoadStatus as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };
      }
      //Packages and containers filters
      // Areas
      if (page === 'packages' || page === 'containers') {
        if (configurationUtils.isModuleActive('AREA')) {
          availableFilters.push({
            key: 'area',
            label: configurationUtils.getPageTitle(true, 'AREA'),
            options: availableOptions!.area,
            singleSelection: true,
            async: true,
          });
          selectedFilters.area = {
            key: 'area',
            label: configurationUtils.getPageTitle(true, 'AREA'),
            visible: true,
            timeAdded: Date.now(),
            values: baseQuery.area
              ? availableOptions!.area.filter((option: any) =>
                  (baseQuery.area as string)!
                    .split(',')
                    .includes(option.id!.toString()),
                )
              : [],
          };
        }
      }

      //Packages only filters

      //Process Steps
      if (page === 'packages') {
        availableFilters.push({
          key: 'processStep',
          label: 'Process Step',
          options: availableOptions!.processStep,
          singleSelection: false,
        });
        selectedFilters.processStep = {
          key: 'processStep',
          label: 'Process Step',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.processStep
            ? availableOptions!.processStep.filter(
                (option: any) =>
                  (baseQuery.processStep as string)!
                    .split(',')
                    .includes(option.name) ||
                  (baseQuery.processStep as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };

        //Manifested
        if (configurationUtils.isModuleActive('MANIFEST')) {
          availableFilters.push({
            key: 'manifested',
            label: 'Manifested',
            options: availableOptions!.packageManifested,
            singleSelection: true,
          });
          selectedFilters.manifested = {
            key: 'manifested',
            label: 'Manifested',
            visible: true,
            timeAdded: Date.now(),
            values: baseQuery.manifested
              ? availableOptions!.packageManifested.filter(
                  (option: any) =>
                    (baseQuery.manifested as string)!
                      .split(',')
                      .includes(option.id),
                )
              : [],
          };
        }

        //Scanned
        availableFilters.push({
          key: 'scanned',
          label: 'Scanned',
          options: availableOptions!.packageScanned,
          singleSelection: true,
        });
        selectedFilters.scanned = {
          key: 'scanned',
          label: 'Scanned',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.scanned
            ? availableOptions!.packageScanned.filter((option: any) =>
                (baseQuery.scanned as string)!
                  .split(',')
                  .includes(option.id),
              )
            : [],
        };

        //Scanned Today
        availableFilters.push({
          key: 'packageScannedToday',
          label: 'Scanned Today',
          options: availableOptions!.packageScannedToday,
          singleSelection: true,
        });
        selectedFilters.packageScannedToday = {
          key: 'packageScannedToday',
          label: 'Scanned Today',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.packageScannedToday
            ? availableOptions!.packageScannedToday.filter(
                (option: any) =>
                  (baseQuery.packageScannedToday as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };

        //Arrived
        availableFilters.push({
          key: 'arrived',
          label: 'Arrived',
          options: availableOptions!.packageArrived,
          singleSelection: true,
        });
        selectedFilters.arrived = {
          key: 'arrived',
          label: 'Arrived',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.arrived
            ? availableOptions!.packageArrived.filter((option: any) =>
                (baseQuery.arrived as string)!
                  .split(',')
                  .includes(option.id),
              )
            : [],
        };

        //In System
        availableFilters.push({
          key: 'in_system',
          label: 'In System',
          options: availableOptions!.packageInSystem,
          singleSelection: true,
        });
        selectedFilters.in_system = {
          key: 'in_system',
          label: 'In System',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.in_system
            ? availableOptions!.packageInSystem.filter(
                (option: any) =>
                  (baseQuery.in_system as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };

        // Packages defects

        availableFilters.push({
          key: 'packageDefect',
          label: 'Defects',
          options: availableOptions!.packageDefect,
          singleSelection: false,
        });
        selectedFilters.packageDefect = {
          key: 'packageDefect',
          label: 'Defects',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.packageDefect
            ? availableOptions!.packageDefect.filter((option: any) =>
                (baseQuery.packageDefect as string)!
                  .split(',')
                  .includes(option.id),
              )
            : [],
        };
      }

      //Containers and manifests filters

      //Type
      if (page === 'containers' || page === 'manifests') {
        availableFilters.push({
          key: 'type',
          label: 'Type',
          options: availableOptions!.containerType,
          singleSelection: true,
        });
        selectedFilters.type = {
          key: 'type',
          label: 'Type',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.type
            ? availableOptions!.containerType.filter((option: any) =>
                (baseQuery.type as string)!
                  .split(',')
                  .includes(option.id),
              )
            : [],
        };
      }

      //Containers only filters

      //Process Step
      if (page === 'containers') {
        availableFilters.push({
          key: 'containerProcessStep',
          label: 'Process Step',
          options: availableOptions!.containerProcessStep,
          singleSelection: false,
        });
        selectedFilters.containerProcessStep = {
          key: 'containerProcessStep',
          label: 'Process Step',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.containerProcessStep
            ? availableOptions!.containerProcessStep.filter(
                (option: any) =>
                  (baseQuery.containerProcessStep as string)!
                    .split(',')
                    .includes(option.id) ||
                  (baseQuery.containerProcessStep as string)!
                    .split(',')
                    .includes(option.name),
              )
            : [],
        };

        //Format
        availableFilters.push({
          key: 'format',
          label: 'Format',
          options: availableOptions!.containerFormat,
          singleSelection: true,
        });
        selectedFilters.format = {
          key: 'format',
          label: 'Format',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.format
            ? availableOptions!.containerFormat.filter(
                (option: any) =>
                  (baseQuery.format as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };

        // Container defects

        availableFilters.push({
          key: 'containerDefect',
          label: 'Defects',
          options: availableOptions!.containerDefect,
          singleSelection: false,
        });
        selectedFilters.containerDefect = {
          key: 'containerDefect',
          label: 'Defects',
          visible: true,
          timeAdded: Date.now(),
          values: baseQuery.containerDefect
            ? availableOptions!.containerDefect.filter(
                (option: any) =>
                  (baseQuery.containerDefect as string)!
                    .split(',')
                    .includes(option.id),
              )
            : [],
        };
      }

      //Set available filters
      setAvailableFilers(availableFilters);

      if (page === 'dashboard') {
        const combinedFilters = {
          ...selectedFilters,
          ...filtersReduxStore.appliedFilters,
        };
        setSelectedFilters(combinedFilters);
        saveFilters(combinedFilters);
      } else {
        setSelectedFilters(selectedFilters);
        saveFilters(selectedFilters);
      }
    } catch (e) {
      handleError(e);
    } finally {
      setShowProgress(false);
    }
  };

  //Anchor el for the add filter menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openAddNewFilterMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const closeAddNewFilterMenu = () => {
    setAnchorEl(null);
  };

  //Redux
  const dispatch = useAppDispatch();
  const filtersReduxStore = useAppSelector(selectGlobalFilters);

  const updateQueryParams = (
    passedInFilters: SelectedFilters,
    resetPage: boolean = false,
  ) => {
    const filtersQuery = createFiltersQuery(passedInFilters);

    //Update URL -- preserve page, sortBy and rows_per_page
    if (baseQuery.page)
      filtersQuery.page = resetPage ? 1 : baseQuery.page;
    if (baseQuery.sortBy) filtersQuery.sortBy = baseQuery.sortBy;
    if (baseQuery.rowsPerPage)
      filtersQuery.rowsPerPage = baseQuery.rowsPerPage;
    browserHistory.replace('?' + queryString.stringify(filtersQuery));
  };

  const saveFilters = (
    passedInFilters = selectedFilters,
    resetPage: boolean = false,
  ) => {
    if (page === 'dashboard') {
      //update global state
      dispatch(updateFiltersState(passedInFilters!));
    } else {
      //logic to send back to main component
      getFilters && getFilters(passedInFilters!);
    }

    updateQueryParams(passedInFilters!, resetPage);
    setOpen(false);
    onAfterClose();
  };

  const loadOptions = (
    inputValue: string,
    callback: any,
    filterKey: string,
  ) => {
    switch (filterKey) {
      case 'route':
        loadRouteOptions(inputValue, callback);
        break;

      case 'carrier':
        loadCarrierOptions(inputValue, callback);
        break;

      case 'stop':
        loadStopOptions(inputValue, callback);
        break;

      case 'owner':
        loadCustomerOptions(inputValue, callback);
        break;

      case 'organization':
        loadOrganizationOptions(inputValue, callback);
        break;

      case 'area':
        loadAreaOptions(inputValue, callback);
        break;

      default:
        break;
    }
  };

  const loadOrganizationOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.organization.values.map(
        (value) => value?.id,
      );
      OrganizationsService.getAllSearch(inputValue)
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: Organization) => {
                return {
                  value: dataEl.id,
                  label: dataEl.name,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  const loadAreaOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.area.values.map(
        (value) => value?.id,
      );
      AreasService.search(inputValue)
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: Area) => {
                return {
                  value: dataEl.id,
                  label: dataEl.name,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  const loadCustomerOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.owner.values.map(
        (value) => value?.id,
      );
      CustomersService.getAll(undefined, inputValue)
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: Customer) => {
                return {
                  value: dataEl.id,
                  label: dataEl.identifier,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  const loadStopOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.stop.values.map(
        (value) => value?.id,
      );
      StationsService.getLocations({
        search: inputValue,
        station: EphemeralStateService.getMyStationId(),
      })
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: Location) => {
                return {
                  value: dataEl.id,
                  label: dataEl.name,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  const loadCarrierOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.carrier.values.map(
        (value) => value?.id,
      );
      FmcService.search(inputValue)
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: User) => {
                return {
                  value: dataEl.id,
                  label: dataEl.full_name,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  const loadRouteOptions = useCallback(
    debounce((inputValue: string, callback: any) => {
      const addedIds = selectedFilters?.route.values.map(
        (value) => value?.id,
      );
      RoutesService.getAll({ search: inputValue })
        .then((data) => {
          callback(
            data.data.results
              .map((dataEl: Route) => {
                return {
                  value: dataEl.id,
                  label: dataEl.name,
                };
              })
              .filter(
                (option) =>
                  !addedIds?.includes(Number(option?.value)),
              ),
          );
        })
        .catch((e) => {
          handleError(e);
        });
    }, 500),
    [selectedFilters],
  );

  useEffect(() => {
    setOpen(isOpen);
    isOpen && setSelectedFiltersInitial(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const combinedFilters = {
      ...selectedFilters,
      ...filtersReduxStore.appliedFilters,
    };
    setSelectedFilters(combinedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReduxStore.appliedFilters]);

  useEffect(() => {
    if (removeFilter && !_.isEmpty(removeFilter)) {
      const updated = {
        ...selectedFilters,
        [removeFilter?.filterKey!]: {
          ...selectedFilters![removeFilter?.filterKey!],
          values: selectedFilters![
            removeFilter?.filterKey!
          ].values.filter((v) => v.id !== removeFilter?.filterValue),
        },
      };

      setSelectedFilters(updated);
      saveFilters(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeFilter]);

  useEffect(() => {
    initFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {showProgress && <ProgressIndicator />}

      <Drawer
        anchor='right'
        open={open}
        onClose={handleDrawerClose}
        onBackdropClick={handleDrawerClose}
        classes={{
          paper: classes.drawerPaper,
        }}
        BackdropProps={{ invisible: true }}
      >
        <Typography
          style={{ fontSize: '0.75em', textTransform: 'uppercase' }}
        >
          Station
        </Typography>
        <Typography variant='h5'>Filters</Typography>
        <hr
          style={{ width: 'calc(100% + 40px)', margin: '20px -20px' }}
        />
        {error && (
          <AlertBanner
            className={classes.banner}
            severity='error'
            alertTitle={'Error'}
            alertMsg={error}
          />
        )}
        {!window.location.pathname.includes(AuthRoutes.MANIFEST) && (
          <Button
            data-testid='filters-drawer:add-new-filter-button'
            variant='text'
            color='primary'
            className={classes.addFilterButton}
            disabled={
              selectedFilters &&
              availableFilters?.length ===
                Object.values(selectedFilters).filter(
                  (filter) => filter.visible,
                ).length
            }
            endIcon={
              <AddCircleIcon
                style={{ color: colors.darkGold, width: '24px' }}
              />
            }
            aria-controls='dasboard-filters-menu'
            aria-haspopup='true'
            onClick={openAddNewFilterMenu}
            style={{
              float: 'right',
            }}
          >
            Add New Filter
          </Button>
        )}
        <Menu
          id='dasboard-filters-menu'
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          getContentAnchorEl={undefined}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={closeAddNewFilterMenu}
        >
          {availableFilters &&
            availableFilters.map(
              (filter) =>
                selectedFilters &&
                selectedFilters[filter.key] &&
                !selectedFilters[filter.key].visible &&
                !selectedFilters[filter.key].values.length && (
                  <MenuItem
                    data-testid={`filters-drawer:add-new:${filter.label}`}
                    key={filter.key}
                    onClick={(e) => {
                      setSelectedFilters({
                        ...selectedFilters,
                        [filter.key]: {
                          ...selectedFilters[filter.key],
                          visible: true,
                          timeAdded: Date.now(),
                        },
                      });
                      closeAddNewFilterMenu();
                    }}
                  >
                    {filter.label}
                  </MenuItem>
                ),
            )}
        </Menu>

        <Box className={classes.filtersHoler}>
          {availableFilters &&
            selectedFilters &&
            availableFilters
              .sort(
                (a, b) =>
                  selectedFilters[a.key].timeAdded -
                  selectedFilters[b.key].timeAdded,
              )
              .map(
                (filter) =>
                  (selectedFilters[filter.key].visible ||
                    selectedFilters[filter.key].values.length !==
                      0) && (
                    <FormControl
                      data-testid={`filters-drawer:${filter.label}`}
                      variant='outlined'
                      className={classes.formControl}
                      key={filter.key}
                    >
                      {filter.async ? (
                        <>
                          <InputLabel
                            className={classes.selectLabel}
                            style={{ fontSize: 12, top: '-25px' }}
                          >
                            {filter.label}
                          </InputLabel>
                          <AsyncSelect<TypeAheadItem>
                            styles={{
                              ...asyncSelectStyles,
                              menuPortal: (styles: any) => {
                                return {
                                  ...styles,
                                  zIndex: 9999,
                                };
                              },
                              control: (styles: any, state: any) => {
                                return {
                                  ...styles,
                                  backgroundColor: 'transparent',
                                  border: '1px solid white',
                                  color: colors.white,
                                  padding: '10px 0',
                                  boxShadow: 'none !important',
                                  outline: `none !important`,
                                  '&:hover': {
                                    border: `1px solid ${colors.white}`,
                                  },
                                  '&:focus': {
                                    border: `1px solid ${colors.white}`,
                                  },
                                };
                              },
                              placeholder: (styles: any) => {
                                return {
                                  ...styles,
                                  color: colors.white,
                                };
                              },
                              loadingIndicator: (styles: any) => {
                                return {
                                  ...styles,
                                  color: colors.white,
                                };
                              },
                              input: (styles: any) => {
                                return {
                                  ...styles,
                                  color: colors.white,
                                };
                              },
                              singleValue: (styles: any) => {
                                return {
                                  ...styles,
                                  color: colors.white,
                                };
                              },
                            }}
                            loadOptions={(
                              inputValue: string,
                              callback: any,
                            ) => {
                              loadOptions(
                                inputValue,
                                callback,
                                filter.key,
                              );
                            }}
                            onChange={(option) => {
                              option &&
                                //update available filter options in store
                                dispatch(
                                  updateFiltersOptions({
                                    ...filtersReduxStore.filterOptions,
                                    [filter.key]: [
                                      ...filtersReduxStore
                                        .filterOptions[filter.key],
                                      {
                                        id: (option as TypeAheadItem)
                                          .value,
                                        name: (
                                          option as TypeAheadItem
                                        ).label,
                                      },
                                    ],
                                  }),
                                );
                              //Does filter allow only 1 selection
                              const singleSelection =
                                availableFilters.find(
                                  (f) => f.key === filter.key,
                                )?.singleSelection;
                              const value = option as TypeAheadItem;
                              setSelectedFilters({
                                ...selectedFilters,
                                [filter.key]: {
                                  ...selectedFilters[filter.key],
                                  values: singleSelection
                                    ? [
                                        {
                                          id: isNaN(
                                            Number(value?.value),
                                          )
                                            ? value.value
                                            : Number(value?.value),
                                          name: value.label,
                                        },
                                      ]
                                    : selectedFilters[
                                        filter.key
                                      ].values.concat({
                                        id: value?.value,
                                        name: value?.label,
                                      }),
                                },
                              });
                            }}
                            placeholder={'Start Typing...'}
                            value={{
                              value: '0',
                              label: 'Start Typing...',
                            }}
                            menuPortalTarget={document.body}
                            components={{ DropdownIndicator }}
                            noOptionsMessage={noOptionsMessage}
                          />
                        </>
                      ) : (
                        <>
                          <InputLabel className={classes.selectLabel}>
                            {filter.label}
                          </InputLabel>
                          <Select
                            value={0}
                            onChange={(e) => {
                              //Cancel if 'Please select' is selected
                              if (e.target.value === '0') return;

                              //Does filter allow only 1 selection
                              const singleSelection =
                                availableFilters.find(
                                  (f) => f.key === filter.key,
                                )?.singleSelection;
                              //Parse id and value
                              const value = (e.target
                                .value as string)!.split('___');
                              setSelectedFilters({
                                ...selectedFilters,
                                [filter.key]: {
                                  ...selectedFilters[filter.key],
                                  values: singleSelection
                                    ? [
                                        {
                                          id: isNaN(Number(value[0]))
                                            ? value[0]
                                            : Number(value[0]),
                                          name: value[1],
                                        },
                                      ]
                                    : selectedFilters[
                                        filter.key
                                      ].values.concat({
                                        id: value[0],
                                        name: value[1],
                                      }),
                                },
                              });
                            }}
                            label={filter.label}
                            className={classes.formSelect}
                            classes={{ icon: classes.selectArrow }}
                          >
                            <MenuItem value='0'>
                              Please select...
                            </MenuItem>

                            {filter.options
                              .filter((item: any) => {
                                return !selectedFilters[
                                  filter.key
                                ].values
                                  .map((value) => value.id.toString())
                                  .includes(item.id!.toString());
                              })
                              .map((item: any) => {
                                const name =
                                  'name' in item
                                    ? item.name
                                    : 'full_name' in item
                                    ? item.full_name
                                    : '';
                                return (
                                  <MenuItem
                                    data-testid={`filters-drawer:${filter.label}:${name}`}
                                    key={item.id}
                                    value={item.id + '___' + name}
                                  >
                                    {name}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </>
                      )}

                      <Box className={classes.filterBadgesHolder}>
                        {selectedFilters[filter.key].values.map(
                          (value) => (
                            <Box
                              data-testid={`filters-drawer:${
                                filter.key
                              }:${
                                value.name || value.full_name
                              }:badge`}
                              key={value.id}
                              className={classes.filterBadge}
                            >
                              {value.name || value.full_name}
                              <Button
                                data-testid={`filters-drawer:${
                                  filter.key
                                }:${
                                  value.name || value.full_name
                                }:remove`}
                                className={classes.filterBadgeButton}
                                onClick={() => {
                                  setSelectedFilters({
                                    ...selectedFilters,
                                    [filter.key]: {
                                      ...selectedFilters[filter.key],
                                      values: selectedFilters[
                                        filter.key
                                      ].values.filter(
                                        (v) => v.id !== value.id,
                                      ),
                                    },
                                  });
                                }}
                              >
                                <ClearIcon />
                              </Button>
                            </Box>
                          ),
                        )}
                      </Box>
                    </FormControl>
                  ),
              )}
        </Box>
        <Box className={classes.filtersButtons}>
          <Button
            variant='contained'
            className={classes.containedButton}
            disabled={false}
            onClick={(e) => {
              handleDrawerClose();
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid='filters-drawer:save-filters'
            variant='outlined'
            className={clx(
              classes.outlinedButton,
              classes.saveButton,
            )}
            disabled={!selectedFilters}
            onClick={(e) => {
              saveFilters(selectedFilters, true);
            }}
          >
            Save
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default withStyles(
  createStyles(() => ({
    ...detailsPageStyle,
    ...filterBadgeStyle,
    drawerPaper: {
      backgroundColor: colors.dark,
      padding: '20px',
      maxWidth: '100%',
      width: '320px',
      color: colors.white,
    },
    addFilterButton: {
      color: colors.white,
      textTransform: 'none',
      width: '150px',
      marginLeft: 'auto',
      marginBottom: '20px',
      '&.Mui-disabled': {
        color: '#999',
        '& svg': {
          color: '#999 !important',
        },
      },
    },
    filtersHoler: {
      overflowY: 'auto',
      paddingTop: '5px',
      paddingRight: '10px',
      marginBottom: '100px',
    },
    filtersButtons: {
      position: 'fixed',
      bottom: 0,
      right: 0,
      padding: '20px',
      backgroundColor: colors.dark,
      textAlign: 'right',
      width: '300px;',
    },
    saveButton: {
      background: colors.darkGold,
      margin: '0 0 8px 8px',
    },
    formControl: {
      width: '100%',
      borderColor: colors.white,
      marginBottom: '20px',
    },
    formSelect: {
      color: colors.white,
      border: `1px solid ${colors.white}`,
    },
    selectLabel: {
      color: `${colors.white}!important`,
      background: colors.dark,
      padding: '0 5px',
    },
    selectArrow: {
      color: colors.white,
    },
  })),
)(FiltersDrawer);
